import { useBusinessEngineApi, usePrivilegedAccessApi } from 'infrastructure/api';
import { ResourceId } from 'framework/crud';
import useCreateRedmineProjectResponseHandler from 'domain/projects/project/redmineproject/useCreateRedmineProjectResponseHandler';
import useCreateTeamsResponseHandler from 'domain/projects/project/redmineproject/useCreateTeamsResponseHandler';
import useCreateGitlabResponseHandler from 'domain/projects/project/redmineproject/useCreateGitlabResponseHandler';
import useCreateSharepointResponseHandler from 'domain/projects/project/redmineproject/useCreateSharepointResponseHandler';
import {
  ProjectToolsCreationCallback,
  ProjectToolMessageCallback,
  ProjectToolType,
  StatusContext,
} from 'domain/projects/shared/projectToolsCreation';
import useResponseMessageHelper from 'domain/projects/project/redmineproject/useResponseMessageHelper';
import redmineProjectValidation from './redmineProjectValidation';
import { useState } from 'react';
import useCreateOnePasswordResponseHandler from './useCreateOnePasswordResponseHandler';
import useCreateAzureGroupsResponseHandler from './useCreateAzureGroupsResponseHandler';
import toolsNewValidation from 'domain/projects/newProject/createupdate/forms/step2/toolsNewValidation';
import { FormFieldsData } from 'framework/form';

export const CREATE_REDMINE_PROJECT = 'CREATE_REDMINE_PROJECT';

const STEPS = {
  VALIDATION: 'VALIDATION',
  CREATION_REDMINE_PROJECT: 'CREATION_REDMINE_PROJECT',
};

const parseCallbackMessage =
  (callback: ProjectToolsCreationCallback, tool: ProjectToolType): ProjectToolMessageCallback =>
  input =>
    callback({ ...input, tool });

const updateStatusContext =
  (statusContext: StatusContext): ProjectToolsCreationCallback =>
  callback => {
    if (!('message' in callback) || !callback.level || !callback.message) {
      return;
    }

    const formattedMessage = <li key={`${callback.level}-creation`}>{callback.message}</li>;
    switch (callback.level) {
      case 'success':
        statusContext.successes.push(formattedMessage);
        return;
      case 'error':
        statusContext.errors.push(formattedMessage);
        return;
      case 'instruction':
        statusContext.successes.push(formattedMessage);
        return;
    }
  };

const useRedmineProjectService = () => {
  const { sendGetRequest } = useBusinessEngineApi();
  const { sendPostRequest } = usePrivilegedAccessApi();
  const { handleCreateProjectResponse } = useCreateRedmineProjectResponseHandler();
  const { handleTeamsResponse } = useCreateTeamsResponseHandler();
  const { handleGitlabResponse } = useCreateGitlabResponseHandler();
  const { handleSharepointResponse } = useCreateSharepointResponseHandler();
  const { handleOnePasswordResponse } = useCreateOnePasswordResponseHandler();
  const { handleAzureGroupsResponse } = useCreateAzureGroupsResponseHandler();
  const { handleStatusContext } = useResponseMessageHelper();
  const [serviceManager, setServiceManager] = useState({
    step: STEPS.VALIDATION,
    isValidationPassed: false,
    isProjectCreated: false,
    projectResponse: {},
    isTeamsCreated: false,
    teamsResponse: {},
    isGitlabCreated: false,
    gitlabResponse: {},
    isSharepointCreated: false,
    sharepointResponse: {},
    isOnePasswordCreated: false,
    onePasswordResponse: {},
    isAzureGroupsCreated: false,
    azureGroupsResponse: {},
  });

  const setStep = (newValue: any) => {
    serviceManager.step = newValue;
  };
  const setIsValidationPassed = (newValue: boolean) => {
    serviceManager.isValidationPassed = newValue;
  };
  const setIsProjectCreated = (newValue: boolean) => {
    serviceManager.isProjectCreated = newValue;
  };
  const setProjectResponse = (newValue: any) => {
    serviceManager.projectResponse = newValue;
  };
  const setIsTeamsCreated = (newValue: boolean) => {
    serviceManager.isTeamsCreated = newValue;
  };
  const setTeamsResponse = (newValue: any) => {
    serviceManager.teamsResponse = newValue;
  };
  const setIsGitlabCreated = (newValue: boolean) => {
    serviceManager.isGitlabCreated = newValue;
  };
  const setGitlabResponse = (newValue: any) => {
    serviceManager.gitlabResponse = newValue;
  };
  const setIsSharepointCreated = (newValue: boolean) => {
    serviceManager.isSharepointCreated = newValue;
  };
  const setSharepointResponse = (newValue: any) => {
    serviceManager.sharepointResponse = newValue;
  };
  const setIsOnePasswordCreated = (newValue: boolean) => {
    serviceManager.isOnePasswordCreated = newValue;
  };
  const setOnePasswordResponse = (newValue: any) => {
    serviceManager.onePasswordResponse = newValue;
  };
  const setIsAzureGroupsCreated = (newValue: boolean) => {
    serviceManager.isAzureGroupsCreated = newValue;
  };
  const setAzureGroupsResponse = (newValue: any) => {
    serviceManager.azureGroupsResponse = newValue;
  };

  const getPrefilledData = async (id: ResourceId) => {
    const response = await sendGetRequest(`/projects/${id}/redmine`);
    return response.json();
  };

  const createWithCallback = async (request: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    if (serviceManager.step === STEPS.VALIDATION && !serviceManager.isValidationPassed) {
      await validationStep(request);
    }

    if (serviceManager.step === STEPS.CREATION_REDMINE_PROJECT) {
      await processRedmineProjectStage(request, callback);
    }

    await processTeamsStage(request, callback);
    await processGitlabStage(request, callback);
    await processSharepointStage(request, callback);
    await processOnePasswordStage(request, callback);
    await processAzureGroupsStage(request, callback);
    setServiceManager(serviceManager);
  };

  const create = async (request: FormFieldsData) => {
    const statusContext: StatusContext = {
      successes: [],
      errors: [],
      instructions: [],
    };

    const callbackFunction = updateStatusContext(statusContext);
    await createWithCallback(request, callbackFunction);
    handleStatusContext(statusContext);
  };

  const validationStep = async (request: FormFieldsData) => {
    const mappedRequest = {
      step: STEPS.VALIDATION,
      identifier: request.identifier,
      davinciProjectId: request.davinciProjectId,
      name: request.name,
      parentIdentifier: request.parentIdentifier,
      parentName: request.accountName || request.parentName,
    };
    const response = await sendPostRequest(`/redmine/project`, mappedRequest);
    const body = await response.json();
    setStep(STEPS.CREATION_REDMINE_PROJECT);
    if (body.validationPassed) {
      setIsValidationPassed(true);
    }

    return body;
  };

  const processRedmineProjectStage = async (mappedRequest: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    callback({ type: 'status', tool: 'Redmine', status: 'in_progress' });
    let redmineProjectResponse = serviceManager.projectResponse;

    if (serviceManager.isValidationPassed && !serviceManager.isProjectCreated) {
      redmineProjectResponse = await createRedmineProject(mappedRequest);
    }

    handleCreateProjectResponse(redmineProjectResponse, parseCallbackMessage(callback, 'Redmine'));
  };

  const createRedmineProject = async (request: FormFieldsData) => {
    const mappedRequest = {
      step: STEPS.CREATION_REDMINE_PROJECT,
      identifier: request.identifier,
      davinciProjectId: request.davinciProjectId,
      name: request.name,
      parentIdentifier: request.parentIdentifier,
      parentName: request.accountName || request.parentName,
    };

    const response = await sendPostRequest(`/redmine/project`, mappedRequest);
    const body = await response.json();

    if (body?.projectCreationStatus?.status === 'OK') {
      setIsProjectCreated(true);
      setProjectResponse(body);
    }
    return body;
  };

  const processTeamsStage = async (mappedRequest: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    callback({ type: 'status', tool: 'Teams', status: 'in_progress' });
    let teamsResponse = serviceManager.teamsResponse;

    if (serviceManager.isProjectCreated && !serviceManager.isTeamsCreated) {
      teamsResponse = await createTeams(mappedRequest);
    }

    handleTeamsResponse(teamsResponse, parseCallbackMessage(callback, 'Teams'));
  };

  const createTeams = async (request: FormFieldsData) => {
    const data = request.isNeedToCreateTeamsGroup
      ? { projectCodeName: request.teamsProjectCodeName }
      : { existedTeamsWebUrl: request.existedTeamsWebUrl };

    const mappedRequest = {
      projectId: request.davinciProjectId,
      ...data,
    };
    const response = await sendPostRequest(`/azure/teams`, mappedRequest);
    const body = await response.json();

    if (body?.teamsCreationStatus?.isCreated) {
      setIsTeamsCreated(true);
      setTeamsResponse(body);
    }
    return body;
  };

  const processSharepointStage = async (mappedRequest: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    callback({ type: 'status', tool: 'Sharepoint', status: 'in_progress' });
    let sharepointResponse = serviceManager.sharepointResponse;

    if (serviceManager.isProjectCreated && !serviceManager.isSharepointCreated) {
      sharepointResponse = await createSharepoint(mappedRequest);
    }

    handleSharepointResponse(sharepointResponse, parseCallbackMessage(callback, 'Sharepoint'));
  };

  const createSharepoint = async (request: FormFieldsData) => {
    const data = request.isNeedToCreateSharepoint
      ? { projectCodeName: request.sharepointProjectCodeName }
      : { existedSharepointWebUrl: request.existedSharepointWebUrl };

    const mappedRequest = {
      projectId: request.davinciProjectId,
      ...data,
    };
    const response = await sendPostRequest(`/azure/sharepoint`, mappedRequest);
    const body = await response.json();

    if (body?.sharepointCreationStatus?.isCreated) {
      setIsSharepointCreated(true);
      setSharepointResponse(body);
    }
    return body;
  };

  const processGitlabStage = async (mappedRequest: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    callback({ type: 'status', tool: 'Gitlab', status: 'in_progress' });
    let gitlabResponse = serviceManager.gitlabResponse;

    if (serviceManager.isProjectCreated && !serviceManager.isGitlabCreated) {
      gitlabResponse = await createGitlab(mappedRequest);
    }

    handleGitlabResponse(gitlabResponse, parseCallbackMessage(callback, 'Gitlab'));
  };

  const createGitlab = async (request: FormFieldsData) => {
    const data = request.isNeedToCreateGitlabGroup
      ? {
          accountAlias: request.gitlabGroupAccountAlias,
          projectCodeName: request.gitlabGroupProjectCodeName,
        }
      : {
          existedGitlabWebUrl: request.existedGitlabWebUrl,
        };

    const mappedRequest = {
      projectId: request.davinciProjectId,
      ...data,
    };

    const response = await sendPostRequest(`/azure/gitlab`, mappedRequest);
    const body = await response.json();

    if (body?.projectGroupCreationStatus?.isCreated) {
      setIsGitlabCreated(true);
      setGitlabResponse(body);
    }
    return body;
  };

  const processOnePasswordStage = async (mappedRequest: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    callback({ type: 'status', tool: '1Password', status: 'in_progress' });
    let onePasswordResponse = serviceManager.onePasswordResponse;

    if (serviceManager.isProjectCreated && !serviceManager.isOnePasswordCreated) {
      onePasswordResponse = await createOnePassword(mappedRequest);
    }

    handleOnePasswordResponse(onePasswordResponse, parseCallbackMessage(callback, '1Password'));
  };

  const createOnePassword = async (request: FormFieldsData) => {
    const mappedRequest = {
      projectId: request.davinciProjectId,
      projectCodeName: request.name,
      needToCreateOnePassword: request.isNeedToCreateOnePassword,
      onePasswordVaults: request.onePasswordVaults || [],
      azureGroups: request.azureGroups || [],
    };

    const response = await sendPostRequest(`/project/one-password`, mappedRequest);
    const body = await response.json();

    const isAzureGroupsCreated = body?.groupsStepStatus?.status === 'SUCCESS';
    const isVaultsCreated = body?.vaultStepStatus?.status === 'SUCCESS';
    const isAccessProvisioned = body?.accessStepStatus?.status === 'SUCCESS';
    if (isAzureGroupsCreated && isVaultsCreated && isAccessProvisioned) {
      setIsOnePasswordCreated(true);
      setOnePasswordResponse(body);
    }
    return body;
  };

  const processAzureGroupsStage = async (mappedRequest: FormFieldsData, callback: ProjectToolsCreationCallback) => {
    let azureGroupsResponse = serviceManager.azureGroupsResponse;

    if (serviceManager.isProjectCreated && !serviceManager.isAzureGroupsCreated) {
      azureGroupsResponse = await createAzureGroups(mappedRequest);
    }

    handleAzureGroupsResponse(azureGroupsResponse, parseCallbackMessage(callback, 'Azure Groups'));
  };

  const createAzureGroups = async (request: FormFieldsData) => {
    const mappedRequest = {
      projectCodeName: request.name,
      projectId: request.davinciProjectId,
      azureGroups: request.azureGroups || [],
    };

    const response = await sendPostRequest(`/project/azure-groups`, mappedRequest);
    const body = await response.json();
    const isAzureGroupsCreated = body?.groupsStepStatus?.status === 'SUCCESS';
    if (isAzureGroupsCreated) {
      setIsAzureGroupsCreated(true);
      setAzureGroupsResponse(body);
    }
    return body;
  };

  const getValidationSchema = async () => {
    return Promise.resolve(redmineProjectValidation);
  };

  const getNewToolsValidationSchema = async () => {
    return Promise.resolve(toolsNewValidation);
  };

  return {
    getPrefilledData,
    create,
    createWithCallback,
    getNewToolsValidationSchema,
    getValidationSchema,
  };
};

export default useRedmineProjectService;
